import type { DragEvent } from 'react';
import _get from 'lodash/get';
import SnapLineManager from '@framework/editor/src/page/index/components/canvas/snapline-manager';
import {
    TDWOperationType,
    TDWModuleType,
    TDWActionType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import type { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import EditProcess from '@src/slide/application/service/edit-action-services/edit-process';
import type { IEditorInstanceService } from '@src/slide/application/service/editor-instance-service/editor-instance-service.interface';
import type { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import type { ISizeInfo } from '@src/slide/application/service/edit-action-services/element-info';
import type EditingStateManager from '@src/slide/application/service/editing-state-manager';
import type { IFrameInfo } from '@src/framework/editor/src/page/index/components/canvas/element-wrapper/utils';
import type { IDragLogicCallback } from './drag-logic.interface';
import type { IMultiRect } from '@src/slide/application/service/edit-action-services/edit-process/edit-process.interface';
import type { IMoveElementManager } from '@src/slide/application/service/move-element-manager/move-element-manager.interface';
import type { ITempElementManager } from '@src/slide/application/service/temp-element-manager/temp-element-manager.interface';
import DragLogic from './drag-logic';
import type { IDragElementHandler } from './drag-element-handler.interface';
import { IGuidesManager } from '@src/slide/application/service/guides-manager/index.interface';
import { MODES } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import { IAbsorbManager } from '@src/slide/application/service/absorb-manager/absorb-manager.interface';
import ElementInfoAdapter from '@src/slide/application/service/edit-action-services/element-info';
import { getGroupChildRelativeProps } from '@src/slide/domain/ui-model/element-modeles/property/group-operation/group-condition';
import { SubscribableService } from '@src/slide/application/service/subscribable-service';
import { DragEventMap, DragEventType } from './drag-element-handler.interface';
import { ILinkManager } from '@src/slide/application/service/link-manager/core/link-manager.interface';
import { ISnapLineService } from '@src/slide/application/service/snap-line-service/interface/snap-line-service.interface';

/**
 * 拖拽元素处理类
 */
export default class DragElementHandler
    extends SubscribableService<DragEventMap> implements IDragElementHandler, IDragLogicCallback {
    /**
     * 当前操作元素model
     */
    private elementModel: IElementBaseModel | null;

    /**
     * 拖拽逻辑对象
     */
    private dragLogic: DragLogic;

    /**
     *  多选元素Rect
     */
    private multiRect: IMultiRect | null;

    /**
     * 是否要等到up事件
     */
    private needMouseUp: boolean;

    /**
     * 通用操作结果逻辑对象
     */
    private editProcessLogic: EditProcess | null;

    /**
     * 回调监听对象
     */
    private handledCallBack?: () => void;
    private handleCallbackAfterDrag?: () => void;

    /**
     * 元素是否在组合内
     */
    private isGroupChildren: boolean;
    private isSmartArtChildren: boolean;

    /**
     * 是否相对于页面
     */
    private dragToSlide: boolean;
    private isPc: boolean;

    private tempElementManager?: ITempElementManager;
    private moveElementManager?: IMoveElementManager;
    private guidesManager?: IGuidesManager;
    private absorbManager?: IAbsorbManager;
    private snapLineService?: ISnapLineService;

    public constructor(
        private selectionService: ISelectionService,
        private editorInstanceService: IEditorInstanceService,
        private editingStateManager: EditingStateManager,
        private linkManager: ILinkManager,
    ) {
        super();
        this.dragLogic = new DragLogic(this);
        this.isPc = browser.isPc;
    }

    public setGuidesManagerDelegate(guidesManager: IGuidesManager): void {
        this.guidesManager = guidesManager;
    }

    public setAbsorbManagerDelegate(absorbManager?: IAbsorbManager): void {
        this.absorbManager = absorbManager;
    }

    public setSnapLineServiceDelegate(snapLineService?: ISnapLineService): void {
        this.snapLineService = snapLineService;
    }

    /**
     * 鼠标按下到元素上之后的处理
     */
    public handleMouseDownOnElement = (
        event: DragEvent,
        elementModel: IElementBaseModel,
        scale: number,
        needMouseUp: boolean,
        callback?: () => void,
        callbackAfterDrag?: () => void,
    ): void => {
        this.initialize(elementModel, callback);
        this.needMouseUp = needMouseUp;
        this.handleCallbackAfterDrag = callbackAfterDrag;
        const { isDraggable, relativeProps, size, sizeOnSlide } = this.getMouseDownProps(elementModel);
        this.dragLogic.handleMouseDownOnElement({
            event, scale, isDraggable,
            relativeProps, size, sizeOnSlide,
            afterDragClear: this.clear,
        });
    };

    /**
     * touch到元素上之后的逻辑
     */
    public handleTouchStartOnElement = (
        event: DragEvent,
        elementModel: IElementBaseModel,
        scale: number,
        needMouseUp: boolean,
        callback: () => void,
        callbackAfterDrag?: () => void,
    ): void => {
        if (this.editorInstanceService.checkEditorIsEditing()) return;
        this.initialize(elementModel, callback);
        this.needMouseUp = needMouseUp;
        this.handleCallbackAfterDrag = callbackAfterDrag;
        const { isDraggable, relativeProps, size, sizeOnSlide } = this.getMouseDownProps(elementModel);
        const isEditing = this.editorInstanceService.getEditorInstance()?.isEditing;
        this.dragLogic.handleTouchStartOnElement({
            event, scale, isDraggable,
            relativeProps, size, sizeOnSlide, isEditing,
            afterDragClear: this.clear,
        });
    };

    /**
     * 开始拖拽逻辑
     */
    public handleDragStart = (elementModel: IElementBaseModel, originalSize: ISizeInfo, e: DragEvent): void => {
        this.initialize(elementModel);
        this.onDragStartOnElement(originalSize, e);
    };

    /**
     * 拖拽中的逻辑
     */
    public handleDrag = (e: DragEvent, originalSize: ISizeInfo, nextSize: ISizeInfo, mode: string): void => {
        this.onDragOnElement(e, originalSize, nextSize, mode);
    };

    /**
     * 拖拽结束的逻辑
     */
    public handleDragStop = (mode: string, e: DragEvent): void => {
        this.onDragStopOnElement(mode, e);
    };

    /**
     * 实现开始接口
     */
    public onDragStartOnElement = (originalSize: ISizeInfo  | null, event: DragEvent): void => {
        // TODO 这里不应该依赖 SnapLinesManager的方法
        // 在 emit 之前将 multiRect 更新
        this.multiRect = SnapLineManager.getInstance().getMultiRect();

        this.emit(DragEventType.DRAG_START, { elementModel: this.elementModel!, e: event, originalSize });
        SnapLineManager.getInstance().calculateSnapLines();
        this.editingStateManager.updateElementDraggingState(true);
        if (this.isGroupChildren) {
            this.handleDragStartForRootGroup(originalSize, event);
            return;
        }
        if (this.isSmartArtChildren) {
            this.handleDragStartForRootGroup(originalSize, event);
            return;
        }
        this.linkManager.linkFloatBarService.hide();
        this.needMouseUp = false;
        if (this.handledCallBack) {
            this.handledCallBack();
        }
        this.moveElementManager?.checkIsDelayUpdatePosing();
        if (this.isPc && this.tempElementManager?.isMoveWithCopy(event)) {
            this.tempElementManager?.addSelectElements();
        }
        this.reportForDragStart();
    };

    /**
     * 实现过程中接口
     */
    public onDragOnElement = (event: DragEvent, originalSize: ISizeInfo, nextSize: ISizeInfo, mode: string): void => {
        this.emit(DragEventType.DRAGGING, {
            elementModel: this.elementModel!,
            e: event,
            nextSize,
            originalSize,
            multiRect: this.multiRect,
            mode,
        });

        let offsetFrame;
        if (this.isPc && this.tempElementManager?.isCopyAndMoving) {
            offsetFrame = this.handleOnCopyAndMoving(event, nextSize, originalSize, offsetFrame, mode);
        } else if (this.isPc && this.tempElementManager?.isMoveWithCopy(event)) {
            this.editProcessLogic?.updateOperationFrame(null);
            this.tempElementManager?.addSelectElements();
        } else {
            offsetFrame = this.editProcessLogic?.runConstraints(event, originalSize, nextSize, mode, this.multiRect);
        }

        if (this.isGroupChildren) {
            this.handleDragForRootGroup(event, originalSize, nextSize, mode, offsetFrame);
        }
        if (this.isSmartArtChildren) {
            this.handleDragForRootGroup(event, originalSize, nextSize, mode, offsetFrame);
        }
    };

    /**
     * 实现结束接口
     * @param mode
     * @param event
     */
    public onDragStopOnElement = (mode: string, event: DragEvent): void => {
        this.emit(DragEventType.DRAG_STOP, { elementModel: this.elementModel!, e: event });

        this.multiRect = null;
        SnapLineManager.getInstance().handleDragResizeStop();
        this.editingStateManager.updateElementDraggingState(false);
        this.handleCallbackAfterDrag?.();
        if (this.isGroupChildren) {
            this.handleDragStopForRootGroup(mode, event);
            return;
        }
        if (this.isSmartArtChildren) {
            this.handleDragStopForRootSmartArt(mode, event);
            return;
        }
        this.editProcessLogic?.persistSize(mode);
        this.editProcessLogic?.updateOperationFrame(null);
        if (this.isPc && this.tempElementManager?.isCopyAndMoving) {
            this.tempElementManager?.pasteElements();
        }
        this.isPc && this.tempElementManager?.clear();
        this.clear();
    };

    public setMoveElementManagerDelegate(moveElementManager: IMoveElementManager): void {
        if (this.moveElementManager) return;
        this.moveElementManager = moveElementManager;
    }

    public setTempElementManagerDelegate(tempElementManager: ITempElementManager): void {
        if (this.tempElementManager) return;
        this.tempElementManager = tempElementManager;
    }

    /**
     * SmartArt 中元素拖拽结束的逻辑
     * @param mode
     * @param e
     */
    public handleDragStopForRootSmartArt = (mode: string, e: DragEvent): void => {
        if (this.dragToSlide) {
            this.onDragStopOnElement(mode, e);
        } else {
            this.editProcessLogic?.persistSizeForSmartArt(this.elementModel?.rootSmartArtModel, false);
            this.editProcessLogic?.updateOperationFrameForGroup(null);
            if (browser.isPc) {
                if (this.tempElementManager?.isMoveWithCopy(e)) {
                    this.tempElementManager?.pasteElements();
                }
                this.tempElementManager?.clear();
            }
        }
        this.dragToSlide = false;
        this.clear();
    };
    /**
     * 组合中元素拖拽开始逻辑
     * @param originalSize
     * @param e
     */
    private handleDragStartForRootGroup = (originalSize: ISizeInfo | null, e: DragEvent): void => {
        this.moveElementManager?.checkIsDelayUpdatePosing();

        if (this.needMouseUp) {
            this.needMouseUp = false;
            this.dragToSlide = true;
        }

        if (this.dragToSlide) {
            this.initialize(this.elementModel?.rootGroupModel, this.handledCallBack);
            this.onDragStartOnElement(originalSize, e);
        } else if (this.isPc && this.tempElementManager?.isMoveWithCopy(e)) {
            this.tempElementManager?.addSelectElements();
        }
        this.reportForGroup();
    };

    /**
     * 组合中元素拖拽过程中逻辑
     */
    private handleDragForRootGroup = (
        e: DragEvent,
        originalSize: ISizeInfo,
        nextSize: ISizeInfo,
        mode: string,
        offsetFrame: IFrameInfo,
    ): void => {
        if (this.isPc && this.tempElementManager?.isCopyAndMoving) {
            this.handleForCopyAndMovingOfRoot(e, nextSize, originalSize, mode, offsetFrame);
        } else if (this.isPc && this.tempElementManager?.isMoveWithCopy(e)) {
            this.editProcessLogic?.updateOperationFrameForGroup(null);
            this.tempElementManager?.addSelectElements();
        } else if (this.isSmartArtChildren) {
            this.editProcessLogic?.runConstraintsForSmartArt(e, originalSize, nextSize, mode, null, offsetFrame);
        } else {
            this.editProcessLogic?.runConstraintsForGroup(e, originalSize, nextSize, mode, null, offsetFrame);
        }
    };

    /**
     * 组合中元素拖拽结束的逻辑
     * @param mode
     * @param e
     */
    private handleDragStopForRootGroup = (mode: string, e: DragEvent): void => {
        this.editProcessLogic?.persistSizeForGroup(this.elementModel?.rootGroupModel, false);
        this.editProcessLogic?.updateOperationFrameForGroup(null);
        if (browser.isPc) {
            if (this.tempElementManager?.isMoveWithCopy(e)) {
                this.tempElementManager?.pasteElements();
            }
            this.tempElementManager?.clear();
        }
        this.clear();
    };

    private initialize(elementModel, handledCallBack?: () => void) {
        this.clear();
        this.elementModel = elementModel;
        this.editProcessLogic = new EditProcess(this.editingStateManager, elementModel);
        this.editProcessLogic.setSelectionService(this.selectionService);
        this.editProcessLogic.setGuidesManagerDelegate(this.guidesManager);
        this.editProcessLogic.setAbsorbManagerDelegate(this.absorbManager);
        this.editProcessLogic.setSnapLineServiceDelegate(this.snapLineService);
        this.isGroupChildren = _get(elementModel, 'parent.type') === UIModelType.GROUP;
        this.isSmartArtChildren = _get(elementModel, 'parent.type') === UIModelType.SMART_ART;
        if (handledCallBack) {
            this.handledCallBack = handledCallBack;
        }
    }

    private handleOnCopyAndMoving(
        event: DragEvent,
        nextSize: ISizeInfo,
        originalSize: ISizeInfo,
        offsetFrame: IFrameInfo,
        mode: string,
    ) {
        let resultFrame = offsetFrame;
        if (this.tempElementManager?.isMoveWithCopy(event)) {
            this.tempElementManager?.move(nextSize.left - originalSize.left, nextSize.top - originalSize.top);
        } else {
            this.tempElementManager?.clear();
            resultFrame = this.editProcessLogic?.runConstraints(event, originalSize, nextSize, mode, this.multiRect);
        }
        return resultFrame;
    }

    /**
     * 上报组合内元素拖拽
     */
    private reportForGroup() {
        if (this.selectionService.selectedElements.selectedArrayForType(SelectedEleMgrType.GROUP).length === 1) {
            // 单选元素
            const [model] = this.selectionService.selectedElements.selectedArrayForType(SelectedEleMgrType.GROUP);
            const ret = this.getReportInfo(model);
            this.reportTDW(ret.tdwModule, ret.action);
        }
    }

    private reportForDragStart() {
        if (this.selectionService.selectedElements.selectedArrayForType(SelectedEleMgrType.SLIDE).length === 1) {
            // 单选元素
            const [model] = this.selectionService.selectedElements.selectedArrayForType(SelectedEleMgrType.SLIDE);
            const ret = this.getReportInfo(model);
            this.reportTDW(ret.tdwModule, ret.action);
        }
    }

    private reportTDW(tdwModuleName: string, actionName: string) {
        if (tdwModuleName && actionName) {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: tdwModuleName,
                action: actionName,
            });
        }
    }

    /**
     * 上报信息生成
     * @param model
     */
    private getReportInfo(model: IElementBaseModel) {
        let tdwModule;
        let action;
        switch (model.type) {
            case UIModelType.SHAPE:
                tdwModule = browser.isMobile ? TDWModuleType.M_SHAPE_EDIT : TDWModuleType.PC_OBJ;
                action = browser.isMobile ? TDWActionType.M_EDIT_MOVE : TDWActionType.SHAPE_MOVE;
                break;
            case UIModelType.IMAGE:
                tdwModule = browser.isMobile ? TDWModuleType.M_PIC_EDIT : TDWModuleType.PC_OBJ;
                action = browser.isMobile ? TDWActionType.M_EDIT_MOVE : TDWActionType.IMG_MOVE;
                break;
            case UIModelType.TABLE:
                tdwModule = browser.isMobile ? TDWModuleType.M_EXCEL_EDIT : TDWModuleType.PC_TABLE_FORMAT;
                action = browser.isMobile ? TDWActionType.M_EDIT_MOVE : TDWActionType.TABLE_MOVE;
                break;
            default:
                tdwModule = null;
                action = null;
                break;
        }
        return { tdwModule, action };
    }

    private handleForCopyAndMovingOfRoot(e, nextSize, originalSize, mode, offsetFrame) {
        if (this.tempElementManager?.isMoveWithCopy(e)) {
            this.tempElementManager?.move(nextSize.left - originalSize.left, nextSize.top - originalSize.top);
        } else {
            this.tempElementManager?.clear();
            this.editProcessLogic?.runConstraintsForGroup(e, originalSize, nextSize, mode, null, offsetFrame);
        }
    }

    /**
     * 清空数据
     */
    private clear = () => {
        this.elementModel = null;
        this.editProcessLogic = null;
        this.isGroupChildren = false;
        this.isSmartArtChildren = false;
        this.dragToSlide = false;
        this.needMouseUp = false;
        this.handledCallBack = undefined;
        this.handleCallbackAfterDrag = undefined;
        this.multiRect = null;
    };

    private getMouseDownProps = (elementModel: IElementBaseModel) => {
        const isDraggable = ElementInfoAdapter.draggable(elementModel);
        const isExistGroup = (this.selectionService.selectedElements
            .selectedArrayForType(SelectedEleMgrType.GROUP).length > 0);
        let relativeProps;
        if (isExistGroup) {
            relativeProps = getGroupChildRelativeProps(elementModel);
        }
        const frame = elementModel.getFrame()?.getFrameInfo();
        const frameOnSlide = elementModel.getFrame()?.frameOnSlide();
        return {
            isDraggable,
            relativeProps,
            size: { width: frame.width, height: frame.height, left: frame.left, top: frame.top },
            sizeOnSlide: { width: frameOnSlide.width, height: frameOnSlide.height,
                left: frameOnSlide.left, top: frameOnSlide.top },
        };
    };
}
